import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import BoxHeader from "../../Components/Box/BoxHeader";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import SelectedUserMenu from "../../Assets/images/menu-icons/manage_accounts_selected.png";
import { AntTab, AntTabs } from "../../Components/Tab/AntTab";
import { GetApiMethod } from "../../Utils/ApiService";
import { showLoader } from "../../Store/loaderSlice";
import { UserDto } from "../../Dto/Generic";
import { textTransform } from "@mui/system";

const UserView = () => {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<UserDto | null>(null);
  const [rejectedPlayers, setRejectedPlayers] = useState<any[]>([]);
  const [acceptedPlayers, setAcceptedPlayers] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [userNames, setUserNames] = useState<{[key: string]: string}>({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchUserName = useCallback(async (userId: string) => {
    if (!userId || userNames[userId]) return userNames[userId] || userId;
    try {
      const response = await GetApiMethod(`user/${userId}`);
      const name = `${response.data.data.firstName} ${response.data.data.lastName}`;
      setUserNames(prev => ({ ...prev, [userId]: name }));
      return name;
    } catch (error) {
      return userId;
    }
  }, [userNames]);
  const formatFieldName = (key:any) => {
  // Insert a space before each capital letter and capitalize the first character
  return key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (str:any) => str.toUpperCase());
};

  const getUser = useCallback(async () => {
    if (!id) return;
    try {
      dispatch(showLoader({ showLoader: true }));
      const res = await GetApiMethod(`user/${id}`);
      setUser(res.data.data);
      dispatch(showLoader({ showLoader: false }));
    } catch (err) {
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch, id]);

  const fetchUserRejections = useCallback(async () => {
    if (!id) return;
    try {
      const res = await GetApiMethod(`user/player-rejections?rejectedById=${id}&limit=1000`);
      setRejectedPlayers(res.data.data.records || []);
    } catch (err) {
      console.error('Error fetching rejections:', err);
    }
  }, [id]);

  const fetchUserAccepts = useCallback(async () => {
    if (!id) return;
    try {
      const res = await GetApiMethod(`user/player-accepts?acceptedById=${id}&limit=1000`);
      const records = res.data.data.records || [];
      
      // Fetch user names for requestFrom IDs
      const userIds = records.map((r: any) => r.requestFrom).filter(Boolean);
      await Promise.all(userIds.map((userId: string) => fetchUserName(userId)));
      
      setAcceptedPlayers(records);
    } catch (err) {
      console.error('Error fetching accepts:', err);
    }
  }, [id, fetchUserName]);

  useEffect(() => {
    getUser();
    fetchUserRejections();
    fetchUserAccepts();
  }, [getUser, fetchUserRejections, fetchUserAccepts]);

  if (!user) return null;

  return (
    <AuthorizedLayout>
      <BoxHeader
        dashboard_name="User Management"
        image={SelectedUserMenu}
      >
        <AntTabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <AntTab label="User Details" className="tab-menu" />
          <AntTab label="Players Rejected" className="tab-menu" />
          <AntTab label="Players Accepted" className="tab-menu" />
        </AntTabs>
      </BoxHeader>
      <Button
        onClick={() => navigate('/user')}
        sx={{ 
          my: 2, 
          color: 'white', 
          backgroundColor: '#09BBE9',
          '&:hover': {
            backgroundColor: '#09BBE9'
          }
        }}
        startIcon={<ArrowBack />}
      >
        Back
      </Button>

      {activeTab === 0 && (
        <TableContainer className="mt-2">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Roles</TableCell>
                <TableCell>
                  {user.roles && user.roles.length > 0
                    ? user.roles.map((r: any) => r.displayName || r.roleName || JSON.stringify(r)).join(", ")
                    : "-"}
                </TableCell>
              </TableRow>
              {Object.entries(user)
                .filter(([key]) =>
                  key !== "id" &&
                  key !== "password" &&
                  key !== "token" &&
                  key !== "status" &&
                  key !== "emailVerified" &&
                  key !== "userColorCode" &&
                  key !== "createdAt" &&
                  key !== "updatedAt" &&
                  key !== "profileImageViewUrl" &&
                  key !== "roles"
                )
                .map(([key, value]) => {
                  let displayValue: string;

                  if (value === null || value === undefined || value === "") {
                    displayValue = "-";
                  } else if (
                    key.toLowerCase() === "date_of_birth" ||
                    key.toLowerCase() === "dateofbirth"
                  ) {
                    const date = new Date(value as string);
                    if (!isNaN(date.getTime())) {
                      displayValue = date.toLocaleDateString("en-AU", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      });
                    } else {
                      displayValue = "-";
                    }
                  } else {
                    displayValue = String(value);
                  }

                  return (
                    <TableRow key={key}>
                      <TableCell>{formatFieldName(key)}</TableCell>
                      <TableCell style={{ textTransform: "capitalize" }}>{displayValue}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {activeTab === 1 && (
        <TableContainer className="mt-2">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Player Name</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Rejected At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rejectedPlayers.map((rejection: any) => (
                <TableRow key={rejection.id}>
                  <TableCell>{rejection.player?.firstName} {rejection.player?.lastName}</TableCell>
                  <TableCell>{rejection.reason || 'No reason provided'}</TableCell>
                  <TableCell>{new Date(rejection.createdAt).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
              {rejectedPlayers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No rejections found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {activeTab === 2 && (
        <TableContainer className="mt-2">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Player Name</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Accepted At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {acceptedPlayers.map((accept: any) => (
                <TableRow key={accept.id}>
                  <TableCell>{userNames[accept.requestFrom] || accept.requestFrom}</TableCell>
                  <TableCell>{accept.message}</TableCell>
                  <TableCell>{new Date(accept.createdAt).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
              {acceptedPlayers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No accepts found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </AuthorizedLayout>
  );
};

export default UserView;